import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  IconButton,
  LinearProgress,
  Dialog,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Autocomplete,
  Chip,
  Grid,
  Fab,
} from '@mui/material';
import { DataGrid, GridRenderCellParams, GridColDef } from '@mui/x-data-grid';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  FilterList as FilterIcon,
  Warning as WarningIcon,
  TrendingUp as TrendingUpIcon,
  Pets as PetsIcon,
  EventBusy as EventBusyIcon,
} from '@mui/icons-material';
import {
  useMortalities,
  useMortalitiesStats,
  useCreateMortality,
  useDeleteMortality,
} from '../hooks/useMortalities';
import { useBirds, useBirdBlocks } from '../hooks/useBirds';
import { Mortality } from '../types';
import { useSearchParams } from 'react-router-dom';

// ─── Design tokens ──────────────────────────────────────────────────────────────

const dataGridSx = {
  border: 'none',
  background: 'transparent',
  '& .MuiDataGrid-cell': {
    borderBottom: '1px solid rgba(255,255,255,0.04)',
    color: '#fff',
    display: 'flex',
    alignItems: 'center',
    '&:focus, &:focus-within': { outline: 'none' },
  },
  '& .MuiDataGrid-columnHeaders': {
    background: 'rgba(244,67,54,0.06)',
    borderBottom: '1px solid rgba(244,67,54,0.15)',
  },
  '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within': {
    outline: 'none',
  },
  '& .MuiDataGrid-columnHeaderTitle': {
    fontWeight: 700,
    fontSize: '0.68rem',
    letterSpacing: '0.7px',
    color: 'rgba(244,67,54,0.65)',
    textTransform: 'uppercase',
  },
  '& .MuiDataGrid-row:hover': { background: 'rgba(244,67,54,0.04)' },
  '& .MuiDataGrid-footerContainer': {
    borderTop: '1px solid rgba(255,152,0,0.1)',
    background: 'rgba(255,152,0,0.03)',
  },
  '& .MuiTablePagination-root, & .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows':
    { color: 'rgba(255,255,255,0.45)', fontSize: '0.78rem' },
  '& .MuiIconButton-root': { color: 'rgba(255,255,255,0.35)' },
  '& .MuiIconButton-root.Mui-disabled': { color: 'rgba(255,255,255,0.12)' },
  '& .MuiDataGrid-virtualScroller': { minHeight: 200 },
};

const formInputSx = {
  '& .MuiOutlinedInput-root': {
    bgcolor: '#111',
    color: '#e0e0e0',
    borderRadius: 1.5,
    '& fieldset': { borderColor: 'rgba(255,255,255,0.1)' },
    '&:hover fieldset': { borderColor: 'rgba(255,152,0,0.4)' },
    '&.Mui-focused fieldset': { borderColor: '#ff9800' },
  },
  '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.4)' },
  '& .MuiInputLabel-root.Mui-focused': { color: '#ff9800' },
  '& .MuiSelect-icon': { color: '#666' },
};

// ─── StatCard ──────────────────────────────────────────────────────────────────

const StatCard: React.FC<{
  label: string;
  value: React.ReactNode;
  sub: string;
  accent: string;
  Icon: React.ElementType;
  faded?: boolean;
}> = ({ label, value, sub, accent, Icon, faded }) => (
  <Paper
    elevation={0}
    sx={{
      flex: 1,
      minWidth: 160,
      p: 2.5,
      background: '#111318',
      border: '1px solid rgba(255,255,255,0.05)',
      borderLeft: `3px solid ${faded ? 'rgba(255,255,255,0.08)' : accent}`,
      borderRadius: 2.5,
      position: 'relative',
      overflow: 'hidden',
      boxShadow: faded ? 'none' : `inset 0 1px 0 ${accent}22`,
    }}
  >
    <Icon
      sx={{
        position: 'absolute',
        right: 12,
        top: '50%',
        transform: 'translateY(-50%)',
        fontSize: 56,
        color: faded ? 'rgba(255,255,255,0.03)' : `${accent}0C`,
      }}
    />
    <Typography
      sx={{
        color: 'rgba(255,255,255,0.35)',
        letterSpacing: '0.7px',
        fontSize: '0.62rem',
        fontWeight: 700,
        textTransform: 'uppercase',
        display: 'block',
        mb: 0.75,
      }}
    >
      {label}
    </Typography>
    <Typography
      fontWeight={800}
      sx={{
        color: faded ? 'rgba(255,255,255,0.2)' : accent,
        lineHeight: 1,
        mb: 0.5,
        fontSize: typeof value === 'number' ? '2rem' : '1.2rem',
      }}
    >
      {value}
    </Typography>
    <Typography sx={{ color: 'rgba(255,255,255,0.28)', fontSize: '0.65rem' }}>{sub}</Typography>
  </Paper>
);

// ─── Detail row (used in view dialog) ──────────────────────────────────────────

const DetailRow: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: 'column',
      gap: 0.4,
      pb: 2,
      borderBottom: '1px solid rgba(255,255,255,0.05)',
      '&:last-child': { pb: 0, borderBottom: 'none' },
    }}
  >
    <Typography
      sx={{
        color: 'rgba(255,255,255,0.3)',
        fontSize: '0.62rem',
        fontWeight: 700,
        letterSpacing: '0.7px',
        textTransform: 'uppercase',
      }}
    >
      {label}
    </Typography>
    {children}
  </Box>
);

// ─── Main component ─────────────────────────────────────────────────────────────

const MortalitiesPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [viewDialog, setViewDialog] = useState(false);
  const [addDialog, setAddDialog] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [selectedMortality, setSelectedMortality] = useState<any>(null);
  const [mortalityToDelete, setMortalityToDelete] = useState<{
    id: number;
    date: string;
  } | null>(null);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 50 });
  const [filters, setFilters] = useState({
    start_date: searchParams.get('start_date') || '',
    end_date: searchParams.get('end_date') || '',
    bird_id: searchParams.get('bird_id') ? Number(searchParams.get('bird_id')) : null,
  });
  const [formData, setFormData] = useState({
    birds_id: '',
    block_id: '' as string,
    quantity: '',
    reason: '',
    recorded_date: new Date().toISOString().split('T')[0],
    remarks: '',
  });

  const {
    data: mortalitiesData,
    error,
    isFetching,
    refetch,
  } = useMortalities({
    page: paginationModel.page + 1,
    per_page: paginationModel.pageSize,
    start_date: filters.start_date || undefined,
    end_date: filters.end_date || undefined,
    birds_id: filters.bird_id || undefined,
  });

  const { data: stats, isFetching: isFetchingStats } = useMortalitiesStats({
    start_date: filters.start_date || undefined,
    end_date: filters.end_date || undefined,
    birds_id: filters.bird_id || undefined,
  });
  const { data: birdsData } = useBirds();
  const selectedBirdForForm = birdsData?.data?.find(
    (b: any) => String(b.id) === String(formData.birds_id)
  );
  const isCagedBird = !!(selectedBirdForForm as any)?.cage_date;
  const { data: birdBlocks = [] } = useBirdBlocks(
    formData.birds_id ? Number(formData.birds_id) : 0
  );
  const cageBlocks = (birdBlocks as any[]).filter((b: any) => (b.type ?? 'rearing') === 'cage');
  const createMutation = useCreateMortality();
  const deleteMutation = useDeleteMortality();

  React.useEffect(() => {
    const params = new URLSearchParams();
    if (filters.start_date) params.set('start_date', filters.start_date);
    if (filters.end_date) params.set('end_date', filters.end_date);
    if (filters.bird_id) params.set('bird_id', filters.bird_id.toString());
    setSearchParams(params, { replace: true });
  }, [filters, setSearchParams]);

  if (error) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <Box textAlign="center" maxWidth={480}>
          <WarningIcon sx={{ fontSize: 56, color: 'rgba(244,67,54,0.4)', mb: 2 }} />
          <Typography variant="h6" sx={{ color: '#fff', mb: 1 }}>
            Failed to Load Mortalities
          </Typography>
          <Typography sx={{ color: 'rgba(255,255,255,0.45)', mb: 3, fontSize: '0.88rem' }}>
            {error instanceof Error ? error.message : 'An error occurred. Please try again.'}
          </Typography>
          <Button
            variant="contained"
            onClick={() => refetch()}
            sx={{
              background: 'linear-gradient(135deg, #f44336 0%, #d32f2f 100%)',
              color: '#fff',
              fontWeight: 700,
              borderRadius: 2,
            }}
          >
            Retry
          </Button>
        </Box>
      </Box>
    );
  }

  const paginationMeta = mortalitiesData?.meta || {
    total: 0,
    current_page: 1,
    last_page: 1,
    per_page: 15,
  };
  const mortalities = mortalitiesData?.data || [];
  const birds = birdsData?.data || [];

  const selectedBird = birds.find((b) => String(b.id) === String(formData.birds_id));
  const minRecordedDate = selectedBird?.date_delivered?.substring(0, 10) ?? undefined;

  const handleView = (mortality: Mortality) => {
    setSelectedMortality(mortality);
    setViewDialog(true);
  };

  const handleCloseDialog = () => {
    setViewDialog(false);
    setSelectedMortality(null);
  };

  const handleCloseAddDialog = () => {
    setAddDialog(false);
    resetForm();
  };

  const handleDeleteClick = (id: number, date: string) => {
    setMortalityToDelete({ id, date });
    setDeleteDialog(true);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialog(false);
    setMortalityToDelete(null);
  };

  const handleConfirmDelete = async () => {
    if (mortalityToDelete) {
      try {
        await deleteMutation.mutateAsync(mortalityToDelete.id);
        handleCloseDeleteDialog();
      } catch (e) {
        console.error(e);
      }
    }
  };

  const resetForm = () =>
    setFormData({
      birds_id: '',
      block_id: '',
      quantity: '',
      reason: '',
      recorded_date: new Date().toISOString().split('T')[0],
      remarks: '',
    });

  const submitMortality = async () => {
    await createMutation.mutateAsync({
      birds_id: Number(formData.birds_id),
      ...(formData.block_id ? { block_id: Number(formData.block_id) } : {}),
      quantity: Number(formData.quantity),
      reason: formData.reason,
      recorded_date: formData.recorded_date,
      remarks: formData.remarks,
    } as any);
  };

  const handleSubmit = async (e: React.SyntheticEvent) => {
    e.preventDefault();
    try {
      await submitMortality();
      handleCloseAddDialog();
    } catch (e) {
      console.error(e);
    }
  };

  const handleSubmitAndAdd = async () => {
    try {
      await submitMortality();
      resetForm();
    } catch (e) {
      console.error(e);
    }
  };

  const handleFilterChange = (field: keyof typeof filters, value: any) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
    setPaginationModel({ page: 0, pageSize: paginationModel.pageSize });
  };

  const handleClearFilters = () => {
    setFilters({ start_date: '', end_date: '', bird_id: null });
    setPaginationModel({ page: 0, pageSize: paginationModel.pageSize });
  };

  const hasActiveFilters = filters.start_date || filters.end_date || filters.bird_id;

  const formatDate = (dateString: string) =>
    dateString
      ? new Date(dateString).toLocaleDateString('en-GB', {
          day: '2-digit',
          month: 'short',
          year: 'numeric',
        })
      : '—';

  const columns: GridColDef[] = [
    { field: 'id', headerName: 'ID', width: 65, minWidth: 65 },
    {
      field: 'procurement_identifier',
      headerName: 'Batch',
      flex: 1,
      minWidth: 130,
      valueGetter: (_, row) => row.birds?.name ?? '—',
      renderCell: (params: GridRenderCellParams) => (
        <Chip
          label={params.value}
          size="small"
          sx={{
            bgcolor: 'rgba(255,152,0,0.1)',
            color: '#ffa726',
            fontWeight: 700,
            fontSize: '0.72rem',
            border: '1px solid rgba(255,152,0,0.2)',
          }}
        />
      ),
    },
    {
      field: 'block',
      headerName: 'Cage Room',
      flex: 0.9,
      minWidth: 120,
      valueGetter: (_, row) => row.block?.name ?? null,
      renderCell: (params: GridRenderCellParams) =>
        params.value ? (
          <Chip
            label={params.value}
            size="small"
            sx={{
              fontSize: '0.7rem',
              height: 20,
              bgcolor: 'rgba(76,175,80,0.1)',
              color: '#66bb6a',
              border: '1px solid rgba(76,175,80,0.2)',
            }}
          />
        ) : (
          <Typography sx={{ color: 'rgba(255,255,255,0.2)', fontSize: '0.78rem' }}>—</Typography>
        ),
    },
    {
      field: 'bird_color',
      headerName: 'Color',
      flex: 0.7,
      minWidth: 90,
      valueGetter: (_, row) => row.birds?.color ?? '—',
      renderCell: (params: GridRenderCellParams) => (
        <Chip
          label={params.value}
          size="small"
          sx={{
            fontWeight: 700,
            fontSize: '0.72rem',
            bgcolor: params.value === 'Brown' ? 'rgba(131,65,5,0.3)' : 'rgba(158,158,158,0.15)',
            color: params.value === 'Brown' ? '#c87941' : '#bdbdbd',
            border: `1px solid ${params.value === 'Brown' ? 'rgba(131,65,5,0.4)' : 'rgba(158,158,158,0.25)'}`,
          }}
        />
      ),
    },
    {
      field: 'quantity',
      headerName: 'Qty Lost',
      flex: 0.6,
      minWidth: 90,
      type: 'number',
      headerAlign: 'center',
      align: 'center',
      renderCell: (params: GridRenderCellParams) => (
        <Typography sx={{ color: '#f44336', fontWeight: 800, fontSize: '0.95rem' }}>
          {params.value}
        </Typography>
      ),
    },
    {
      field: 'reason',
      headerName: 'Cause / Reason',
      flex: 1.5,
      minWidth: 180,
      valueGetter: (value) => value || '—',
      renderCell: (params: GridRenderCellParams) => (
        <Typography sx={{ color: 'rgba(255,255,255,0.65)', fontSize: '0.82rem' }}>
          {params.value}
        </Typography>
      ),
    },
    {
      field: 'recorded_date',
      headerName: 'Date',
      flex: 0.9,
      minWidth: 120,
      renderCell: (params: GridRenderCellParams) => (
        <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.8rem' }}>
          {formatDate(params.value)}
        </Typography>
      ),
    },
    {
      field: 'added_by',
      headerName: 'Recorded By',
      flex: 1,
      minWidth: 130,
      valueGetter: (_, row) => row.added_by?.name || '—',
      renderCell: (params: GridRenderCellParams) => (
        <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.8rem' }}>
          {params.value}
        </Typography>
      ),
    },
    {
      field: 'actions',
      headerName: '',
      width: 90,
      minWidth: 90,
      sortable: false,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params: GridRenderCellParams) => (
        <Box display="flex" gap={0.5}>
          <IconButton
            size="small"
            onClick={() => handleView(params.row)}
            sx={{
              color: '#ff9800',
              bgcolor: 'rgba(255,152,0,0.08)',
              border: '1px solid rgba(255,152,0,0.18)',
              width: 30,
              height: 30,
              '&:hover': { bgcolor: 'rgba(255,152,0,0.18)' },
            }}
          >
            <ViewIcon sx={{ fontSize: 15 }} />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => handleDeleteClick(params.row.id, params.row.mortality_date)}
            disabled={deleteMutation.isPending}
            sx={{
              color: '#f44336',
              bgcolor: 'rgba(244,67,54,0.08)',
              border: '1px solid rgba(244,67,54,0.18)',
              width: 30,
              height: 30,
              '&:hover': { bgcolor: 'rgba(244,67,54,0.18)' },
            }}
          >
            <DeleteIcon sx={{ fontSize: 15 }} />
          </IconButton>
        </Box>
      ),
    },
  ];

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      {isFetching && (
        <LinearProgress
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 9999,
            height: 3,
            bgcolor: 'rgba(244,67,54,0.1)',
            '& .MuiLinearProgress-bar': { background: 'linear-gradient(90deg, #f44336, #ef5350)' },
          }}
        />
      )}

      {/* ── Page header ── */}
      <Box
        sx={{
          mb: 3,
          p: { xs: 2.5, md: 3 },
          borderRadius: 3,
          background: 'linear-gradient(135deg, #131316 0%, #1c1c22 100%)',
          border: '1px solid rgba(244,67,54,0.15)',
          boxShadow: '0 4px 24px rgba(0,0,0,0.4)',
          display: 'flex',
          alignItems: 'center',
          gap: 2.5,
          flexWrap: 'wrap',
        }}
      >
        <Box
          sx={{
            width: 54,
            height: 54,
            borderRadius: 2,
            flexShrink: 0,
            background:
              'linear-gradient(135deg, rgba(244,67,54,0.2) 0%, rgba(244,67,54,0.08) 100%)',
            border: '1px solid rgba(244,67,54,0.25)',
            boxShadow: '0 4px 16px rgba(244,67,54,0.2)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <WarningIcon sx={{ color: '#f44336', fontSize: 28 }} />
        </Box>
        <Box flex={1} minWidth={0}>
          <Typography
            variant="h5"
            fontWeight={800}
            sx={{ color: '#fff', letterSpacing: '-0.02em', lineHeight: 1.2 }}
          >
            Mortalities
          </Typography>
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.38)', mt: 0.3 }}>
            Track bird losses, causes and mortality rates over time
          </Typography>
        </Box>
        {paginationMeta.total > 0 && (
          <Chip
            label={`${paginationMeta.total} records`}
            size="small"
            sx={{
              bgcolor: 'rgba(244,67,54,0.12)',
              color: '#f44336',
              fontWeight: 700,
              border: '1px solid rgba(244,67,54,0.22)',
              fontSize: '0.75rem',
            }}
          />
        )}
      </Box>

      {/* ── Stats strip ── */}
      <Box
        sx={{
          position: 'relative',
          mb: 3,
          opacity: isFetchingStats ? 0.6 : 1,
          transition: 'opacity 0.3s',
        }}
      >
        {isFetchingStats && (
          <LinearProgress
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: 2,
              borderRadius: '2px 2px 0 0',
              bgcolor: 'transparent',
              '& .MuiLinearProgress-bar': {
                background: 'linear-gradient(90deg, #f44336, #ff9800)',
              },
            }}
          />
        )}
        <Box display="flex" gap={2.5} flexWrap="wrap">
          <StatCard
            label="Total Deaths"
            value={stats?.total_mortalities ?? 0}
            sub="birds lost"
            accent="#f44336"
            Icon={WarningIcon}
            faded={!stats}
          />
          <StatCard
            label="Mortality Rate"
            value={
              stats?.overall_mortality_rate
                ? `${Number(stats.overall_mortality_rate).toFixed(2)}%`
                : '0.00%'
            }
            sub="overall rate"
            accent="#ff9800"
            Icon={TrendingUpIcon}
            faded={!stats}
          />
          <StatCard
            label="Total Incidents"
            value={stats?.total_records ?? 0}
            sub="mortality records"
            accent="#42a5f5"
            Icon={PetsIcon}
            faded={!stats}
          />
          <StatCard
            label="This Month"
            value={stats?.current_month_mortalities ?? 0}
            sub="recent deaths"
            accent="#ba68c8"
            Icon={EventBusyIcon}
            faded={!stats}
          />
        </Box>
      </Box>

      {/* ── Top Causes ── */}
      {stats?.top_causes && stats.top_causes.length > 0 && (
        <Paper
          elevation={0}
          sx={{
            mb: 3,
            borderRadius: 2.5,
            border: '1px solid rgba(244,67,54,0.12)',
            background: '#0e0e10',
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              px: 2.5,
              py: 1.5,
              borderBottom: '1px solid rgba(244,67,54,0.1)',
              background: 'rgba(244,67,54,0.04)',
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <WarningIcon sx={{ color: '#f44336', fontSize: 16 }} />
            <Typography
              sx={{
                color: 'rgba(244,67,54,0.7)',
                fontSize: '0.62rem',
                fontWeight: 700,
                letterSpacing: '0.7px',
                textTransform: 'uppercase',
              }}
            >
              Top Causes of Mortality
            </Typography>
          </Box>
          <Box sx={{ p: 2, display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
            {stats.top_causes.slice(0, 5).map((cause: any, index: number) => (
              <Box
                key={index}
                sx={{
                  flex: '1 1 160px',
                  p: 2,
                  borderRadius: 2,
                  background: '#111318',
                  border: '1px solid rgba(244,67,54,0.12)',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 0.75,
                }}
              >
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Chip
                    label={`#${index + 1}`}
                    size="small"
                    sx={{
                      height: 18,
                      fontSize: '0.6rem',
                      fontWeight: 800,
                      bgcolor: index === 0 ? 'rgba(244,67,54,0.2)' : 'rgba(255,255,255,0.06)',
                      color: index === 0 ? '#f44336' : 'rgba(255,255,255,0.4)',
                      border: `1px solid ${index === 0 ? 'rgba(244,67,54,0.3)' : 'rgba(255,255,255,0.08)'}`,
                    }}
                  />
                  <Typography
                    sx={{
                      color: '#f44336',
                      fontWeight: 800,
                      fontSize: '1.1rem',
                      fontFamily: 'monospace',
                      lineHeight: 1,
                    }}
                  >
                    {cause.total_deaths}
                  </Typography>
                </Box>
                <Typography
                  sx={{
                    color: '#fff',
                    fontWeight: 600,
                    fontSize: '0.8rem',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {cause.reason}
                </Typography>
                <Typography sx={{ color: 'rgba(255,255,255,0.28)', fontSize: '0.65rem' }}>
                  deaths
                </Typography>
              </Box>
            ))}
          </Box>
        </Paper>
      )}

      {/* ── Filters ── */}
      <Paper
        elevation={0}
        sx={{
          mb: 3,
          borderRadius: 2.5,
          border: `1px solid ${hasActiveFilters ? 'rgba(255,152,0,0.25)' : 'rgba(255,255,255,0.06)'}`,
          background: '#0e0e10',
          overflow: 'hidden',
          transition: 'border-color 0.2s',
        }}
      >
        <Box
          sx={{
            px: 2.5,
            py: 1.5,
            borderBottom: '1px solid rgba(255,255,255,0.04)',
            background: hasActiveFilters ? 'rgba(255,152,0,0.04)' : 'rgba(255,255,255,0.02)',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <FilterIcon
            sx={{ fontSize: 15, color: hasActiveFilters ? '#ff9800' : 'rgba(255,255,255,0.25)' }}
          />
          <Typography
            sx={{
              fontSize: '0.62rem',
              fontWeight: 700,
              letterSpacing: '0.7px',
              textTransform: 'uppercase',
              color: hasActiveFilters ? 'rgba(255,152,0,0.7)' : 'rgba(255,255,255,0.25)',
            }}
          >
            Filters
          </Typography>
          {hasActiveFilters && (
            <Chip
              label="Active"
              size="small"
              sx={{
                height: 17,
                fontSize: '0.6rem',
                fontWeight: 700,
                bgcolor: 'rgba(255,152,0,0.15)',
                color: '#ff9800',
                border: '1px solid rgba(255,152,0,0.3)',
              }}
            />
          )}
        </Box>
        <Box sx={{ p: 2.5 }}>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <TextField
                fullWidth
                type="date"
                label="Start Date"
                size="small"
                slotProps={{ inputLabel: { shrink: true } }}
                value={filters.start_date}
                onChange={(e) => handleFilterChange('start_date', e.target.value)}
                sx={formInputSx}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <TextField
                fullWidth
                type="date"
                label="End Date"
                size="small"
                slotProps={{ inputLabel: { shrink: true } }}
                value={filters.end_date}
                onChange={(e) => handleFilterChange('end_date', e.target.value)}
                sx={formInputSx}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Autocomplete
                options={birds}
                getOptionLabel={(option) => option.name}
                value={birds.find((b) => b.id === filters.bird_id) || null}
                onChange={(_, newValue) => handleFilterChange('bird_id', newValue?.id || null)}
                size="small"
                renderInput={(params) => (
                  <TextField {...params} label="Bird Batch" sx={formInputSx} />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 2 }}>
              <Button
                fullWidth
                variant="outlined"
                onClick={handleClearFilters}
                disabled={!hasActiveFilters}
                sx={{
                  height: 40,
                  color: 'rgba(255,255,255,0.45)',
                  borderColor: 'rgba(255,255,255,0.1)',
                  borderRadius: 1.5,
                  textTransform: 'none',
                  fontWeight: 600,
                  '&:hover': {
                    borderColor: 'rgba(255,255,255,0.25)',
                    bgcolor: 'rgba(255,255,255,0.04)',
                  },
                  '&:disabled': {
                    color: 'rgba(255,255,255,0.2)',
                    borderColor: 'rgba(255,255,255,0.06)',
                  },
                }}
              >
                Clear
              </Button>
            </Grid>
          </Grid>
        </Box>
      </Paper>

      {/* ── DataGrid ── */}
      <Paper
        elevation={0}
        sx={{
          borderRadius: 2.5,
          border: '1px solid rgba(244,67,54,0.12)',
          background: '#0e0e10',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            px: 2.5,
            py: 2,
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            borderBottom: '1px solid rgba(244,67,54,0.1)',
            background: 'rgba(244,67,54,0.04)',
          }}
        >
          <WarningIcon sx={{ color: '#f44336', fontSize: 17 }} />
          <Typography fontWeight={700} sx={{ color: '#fff', fontSize: '0.9rem' }}>
            Mortality Records
          </Typography>
          <Chip
            label={paginationMeta.total}
            size="small"
            sx={{
              bgcolor: 'rgba(244,67,54,0.12)',
              color: '#f44336',
              fontWeight: 700,
              height: 20,
              fontSize: '0.72rem',
            }}
          />
        </Box>
        <DataGrid
          rows={mortalities}
          columns={columns}
          rowCount={paginationMeta.total}
          paginationMode="server"
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[10, 25, 50]}
          disableRowSelectionOnClick
          loading={isFetching}
          autoHeight
          sx={dataGridSx}
        />
      </Paper>

      {/* ── FAB ── */}
      <Fab
        onClick={() => setAddDialog(true)}
        sx={{
          position: 'fixed',
          bottom: 32,
          right: 32,
          background: 'linear-gradient(135deg, #f44336 0%, #d32f2f 100%)',
          color: '#fff',
          boxShadow: '0 6px 20px rgba(244,67,54,0.45)',
          '&:hover': {
            background: 'linear-gradient(135deg, #ef5350 0%, #f44336 100%)',
            boxShadow: '0 8px 28px rgba(244,67,54,0.55)',
          },
        }}
      >
        <AddIcon />
      </Fab>

      {/* ══ View dialog ══ */}
      <Dialog
        open={viewDialog}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
        slotProps={{
          paper: {
            sx: {
              borderRadius: 3,
              background: '#0e0e10',
              backgroundImage: 'none',
              border: '1px solid rgba(255,152,0,0.2)',
            },
          },
        }}
      >
        <Box
          sx={{
            px: 3,
            pt: 3,
            pb: 2,
            borderBottom: '1px solid rgba(255,152,0,0.1)',
            background: 'linear-gradient(135deg, rgba(255,152,0,0.06) 0%, transparent 100%)',
          }}
        >
          <Typography variant="h6" fontWeight={800} sx={{ color: '#fff' }}>
            Mortality Details
          </Typography>
          {selectedMortality && (
            <Typography variant="caption" sx={{ color: '#ff9800', fontWeight: 600 }}>
              {formatDate(selectedMortality.recorded_date ?? selectedMortality.mortality_date)}
            </Typography>
          )}
        </Box>
        <DialogContent sx={{ pt: 3 }}>
          {selectedMortality && (
            <Box display="flex" flexDirection="column" gap={2}>
              <DetailRow label="Procurement Batch">
                <Typography sx={{ color: '#fff', fontWeight: 700 }}>
                  {selectedMortality.procurement_identifier}
                </Typography>
              </DetailRow>
              <DetailRow label="Bird Color">
                <Chip
                  label={selectedMortality.bird_color}
                  size="small"
                  sx={{
                    alignSelf: 'flex-start',
                    fontWeight: 700,
                    bgcolor:
                      selectedMortality.bird_color === 'Brown'
                        ? 'rgba(131,65,5,0.3)'
                        : 'rgba(158,158,158,0.15)',
                    color: selectedMortality.bird_color === 'Brown' ? '#c87941' : '#bdbdbd',
                    border: `1px solid ${selectedMortality.bird_color === 'Brown' ? 'rgba(131,65,5,0.4)' : 'rgba(158,158,158,0.25)'}`,
                  }}
                />
              </DetailRow>
              {selectedMortality.block && (
                <DetailRow label="Cage Room">
                  <Chip
                    label={selectedMortality.block.name}
                    size="small"
                    sx={{
                      alignSelf: 'flex-start',
                      fontWeight: 700,
                      bgcolor: 'rgba(76,175,80,0.1)',
                      color: '#66bb6a',
                      border: '1px solid rgba(76,175,80,0.2)',
                    }}
                  />
                </DetailRow>
              )}
              <DetailRow label="Quantity Lost">
                <Typography
                  sx={{ color: '#f44336', fontWeight: 800, fontSize: '1.8rem', lineHeight: 1 }}
                >
                  {selectedMortality.quantity}
                </Typography>
              </DetailRow>
              {selectedMortality.survival_rate != null && (
                <DetailRow label="Survival Rate">
                  <Typography sx={{ color: '#66bb6a', fontWeight: 700, fontSize: '1rem' }}>
                    {Number(selectedMortality.survival_rate).toFixed(2)}%
                  </Typography>
                </DetailRow>
              )}
              <DetailRow label="Cause / Reason">
                <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.9rem' }}>
                  {selectedMortality.cause || selectedMortality.reason || 'Not specified'}
                </Typography>
              </DetailRow>
              <DetailRow label="Recorded By">
                <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.88rem' }}>
                  {typeof selectedMortality.added_by === 'object'
                    ? selectedMortality.added_by?.name
                    : selectedMortality.added_by || '—'}
                </Typography>
              </DetailRow>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, borderTop: '1px solid rgba(255,255,255,0.06)' }}>
          <Button
            onClick={handleCloseDialog}
            sx={{
              color: 'rgba(255,255,255,0.5)',
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: 1.5,
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* ══ Add dialog ══ */}
      <Dialog
        open={addDialog}
        onClose={handleCloseAddDialog}
        maxWidth="sm"
        fullWidth
        slotProps={{
          paper: {
            sx: {
              borderRadius: 3,
              background: '#0e0e10',
              backgroundImage: 'none',
              border: '1px solid rgba(244,67,54,0.2)',
            },
          },
        }}
      >
        <form onSubmit={handleSubmit}>
          <Box
            sx={{
              px: 3,
              pt: 3,
              pb: 2,
              borderBottom: '1px solid rgba(244,67,54,0.1)',
              background: 'linear-gradient(135deg, rgba(244,67,54,0.07) 0%, transparent 100%)',
            }}
          >
            <Typography variant="h6" fontWeight={800} sx={{ color: '#fff' }}>
              Record Mortality
            </Typography>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.38)' }}>
              Enter mortality details below
            </Typography>
          </Box>
          <DialogContent sx={{ pt: 3 }}>
            <Box display="flex" flexDirection="column" gap={2.5}>
              <TextField
                select
                required
                fullWidth
                size="small"
                label="Bird Batch"
                value={formData.birds_id}
                onChange={(e) =>
                  setFormData({ ...formData, birds_id: e.target.value, block_id: '' })
                }
                sx={formInputSx}
              >
                {birds.map((bird) => (
                  <MenuItem key={bird.id} value={bird.id}>
                    {bird.name}
                  </MenuItem>
                ))}
              </TextField>
              {isCagedBird && cageBlocks.length > 0 && (
                <TextField
                  select
                  required
                  fullWidth
                  size="small"
                  label="Cage Room *"
                  value={formData.block_id}
                  onChange={(e) => setFormData({ ...formData, block_id: e.target.value })}
                  helperText="Mortality will be deducted from this cage's bird count"
                  FormHelperTextProps={{
                    sx: { color: 'rgba(255,255,255,0.3)', fontSize: '0.68rem' },
                  }}
                  sx={formInputSx}
                >
                  {cageBlocks.map((block: any) => (
                    <MenuItem key={block.id} value={block.id}>
                      {block.name}
                      <Typography
                        component="span"
                        sx={{ color: 'rgba(255,255,255,0.35)', fontSize: '0.75rem', ml: 1 }}
                      >
                        · {block.current_quantity?.toLocaleString()} birds
                      </Typography>
                    </MenuItem>
                  ))}
                </TextField>
              )}
              <TextField
                required
                fullWidth
                size="small"
                type="number"
                label="Quantity"
                value={formData.quantity}
                onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
                slotProps={{ htmlInput: { min: 1 } }}
                sx={formInputSx}
              />
              <TextField
                required
                fullWidth
                size="small"
                label="Reason"
                value={formData.reason}
                onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                placeholder="e.g. Disease, Predator, Natural causes"
                sx={formInputSx}
              />
              <TextField
                required
                fullWidth
                size="small"
                type="date"
                label="Recorded Date"
                slotProps={{
                  inputLabel: { shrink: true },
                  htmlInput: { min: minRecordedDate },
                }}
                value={formData.recorded_date}
                onChange={(e) => setFormData({ ...formData, recorded_date: e.target.value })}
                helperText={
                  minRecordedDate
                    ? `Cannot be before delivery date (${minRecordedDate})`
                    : undefined
                }
                FormHelperTextProps={{
                  sx: { color: 'rgba(255,255,255,0.3)', fontSize: '0.68rem' },
                }}
                sx={formInputSx}
              />
              <TextField
                fullWidth
                size="small"
                multiline
                rows={3}
                label="Remarks"
                value={formData.remarks}
                onChange={(e) => setFormData({ ...formData, remarks: e.target.value })}
                placeholder="Additional details or observations…"
                sx={formInputSx}
              />
            </Box>
          </DialogContent>
          <DialogActions
            sx={{ px: 3, pb: 2.5, gap: 1, borderTop: '1px solid rgba(255,255,255,0.06)' }}
          >
            <Button
              onClick={handleCloseAddDialog}
              sx={{ color: 'rgba(255,255,255,0.5)', textTransform: 'none', fontWeight: 600 }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmitAndAdd}
              disabled={
                createMutation.isPending ||
                !formData.birds_id ||
                !formData.quantity ||
                !formData.reason ||
                !formData.recorded_date
              }
              sx={{
                color: 'rgba(255,255,255,0.6)',
                border: '1px solid rgba(255,255,255,0.12)',
                borderRadius: 1.5,
                px: 2.5,
                textTransform: 'none',
                fontWeight: 600,
                '&:hover': {
                  borderColor: 'rgba(255,255,255,0.25)',
                  bgcolor: 'rgba(255,255,255,0.04)',
                },
                '&:disabled': {
                  color: 'rgba(255,255,255,0.2)',
                  borderColor: 'rgba(255,255,255,0.06)',
                },
              }}
            >
              Save & Add Another
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={createMutation.isPending}
              sx={{
                background: 'linear-gradient(135deg, #f44336 0%, #d32f2f 100%)',
                color: '#fff',
                fontWeight: 700,
                borderRadius: 1.5,
                px: 3,
                textTransform: 'none',
                '&:hover': { background: 'linear-gradient(135deg, #ef5350 0%, #f44336 100%)' },
                '&:disabled': { background: 'rgba(244,67,54,0.2)', color: 'rgba(255,255,255,0.3)' },
              }}
            >
              {createMutation.isPending ? 'Recording…' : 'Record Mortality'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* ══ Delete dialog ══ */}
      <Dialog
        open={deleteDialog}
        onClose={handleCloseDeleteDialog}
        maxWidth="xs"
        fullWidth
        slotProps={{
          paper: {
            sx: {
              borderRadius: 3,
              background: '#0e0e10',
              backgroundImage: 'none',
              border: '1px solid rgba(244,67,54,0.25)',
            },
          },
        }}
      >
        <Box
          sx={{
            px: 3,
            pt: 3,
            pb: 2,
            borderBottom: '1px solid rgba(244,67,54,0.12)',
            background: 'linear-gradient(135deg, rgba(244,67,54,0.07) 0%, transparent 100%)',
          }}
        >
          <Typography variant="h6" fontWeight={800} sx={{ color: '#f44336' }}>
            Delete Record
          </Typography>
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.38)' }}>
            This action cannot be undone
          </Typography>
        </Box>
        <DialogContent sx={{ pt: 2.5 }}>
          <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.88rem' }}>
            Are you sure you want to delete the mortality record from{' '}
            <strong style={{ color: '#fff' }}>
              {mortalityToDelete && formatDate(mortalityToDelete.date)}
            </strong>
            ?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1, borderTop: '1px solid rgba(244,67,54,0.1)' }}>
          <Button
            onClick={handleCloseDeleteDialog}
            sx={{ color: 'rgba(255,255,255,0.5)', textTransform: 'none', fontWeight: 600 }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDelete}
            variant="contained"
            disabled={deleteMutation.isPending}
            sx={{
              background: 'linear-gradient(135deg, #f44336 0%, #d32f2f 100%)',
              color: '#fff',
              fontWeight: 700,
              borderRadius: 1.5,
              px: 3,
              textTransform: 'none',
              '&:hover': { background: 'linear-gradient(135deg, #ef5350 0%, #f44336 100%)' },
              '&:disabled': { background: 'rgba(244,67,54,0.2)', color: 'rgba(255,255,255,0.3)' },
            }}
          >
            {deleteMutation.isPending ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MortalitiesPage;
